import { useAppConfig } from 'providers/AppConfig';
import React, { createContext, type FC, useState, useCallback } from 'react';
import { type RegionConfig } from './interfaces';
import { useSessionFlag } from 'hooks/useSessionFlag';
import { mockRegions } from './mockRegions';
import { OFFICE_TYPE, REGIONS_WITH_SUB_REGIONS, type ValidAppRegion } from 'utils/regionUtils';
import { type Address } from 'components/ProviderProfile/interfaces';

interface RegionConfigProviderProps {}

interface RegionConfigContextProps extends RegionConfig {
    includeProviderAddresses?: (addresses: Address[]) => void;
}

const RegionConfigContext = createContext<RegionConfigContextProps>({
    defaultLng: '',
    languages: [],
    providerContactUrl: '',
    helpCenterNumber: '1-800-234-5154',
    features: [],
    techSupportUrls: {
        en: { url: '', vanityUrl: '' },
        fr: { url: '', vanityUrl: '' }
    },
    helpUrls: {
        en: { url: '', vanityUrl: '' },
        fr: { url: '', vanityUrl: '' }
    },
});

RegionConfigContext.displayName = 'RegionConfigContext';

function mapSubRegionsConfigByProviderAddresses(existingConfig: RegionConfig, providerAddresses: Address[] = []): Partial<RegionConfig> {
    if (REGIONS_WITH_SUB_REGIONS.includes(existingConfig.defaultLng as ValidAppRegion)) {
        const [mainLang, mainCountryCode] = existingConfig.defaultLng.split('-');
        const alternateCountryCode = providerAddresses
                                        .filter(addr => addr.officeType === OFFICE_TYPE.CLINICAL_OFFICE_EXTERNAL)
                                        .find(addr => addr.countryCode !== mainCountryCode)?.countryCode;
        if (alternateCountryCode) {
            const { languages } = existingConfig;
            localStorage.removeItem('i18nextLng');
            return {
                ...existingConfig,
                defaultLng: `${mainLang}-${alternateCountryCode}`,
                languages: [{
                    name: languages[0].name.replace(/\([A-Z]{2}\)/, `(${alternateCountryCode})`),
                    code: `${mainLang}-${alternateCountryCode}`,
                    fallback: [mainLang]
                }]
            } as RegionConfig
        }
    }

    return existingConfig as Partial<RegionConfig>;
}

const RegionConfigProvider: FC<RegionConfigProviderProps> = (props) => {
    const { RegionConfigs } = useAppConfig();
    const [providerAddresses, setProviderAddresses] = useState<Address[]>([]);
    const forcedRegion = useSessionFlag<ValidAppRegion>('forced-region');
    const regionConfig = mapSubRegionsConfigByProviderAddresses(
        { ...(RegionConfigs[window.location.host] ?? {}), ...(mockRegions[forcedRegion] ?? {}) },
        providerAddresses
    );

    const includeProviderAddresses = useCallback((addresses: Address[]) => {
        setProviderAddresses(addresses);
    }, []);

    const contextValue: RegionConfigContextProps = {
        providerContactUrl: '',
        techSupportUrls: {
            en: { url: '', vanityUrl: '' },
            fr: { url: '', vanityUrl: '' }
        },
        helpUrls: {
            en: { url: '', vanityUrl: '' },
            fr: { url: '', vanityUrl: '' }
        },
        defaultLng: 'en-US',
        languages: [{
            name: "English",
            code: "en-US",
            fallback: ["en"]
        }],
        helpCenterNumber: '1-800-234-5154',
        features: [],
        ...regionConfig,
        includeProviderAddresses
    }

    return <RegionConfigContext.Provider value={contextValue} {...props} />
}


const useRegionConfig = () => React.useContext(RegionConfigContext);

export { RegionConfigProvider, useRegionConfig, RegionConfigContext };